import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { NavigationReason } from '@spotify-internal/ubi-types-js';

import { PageId } from './pageId';
import { useUbiContext } from './ubiProvider';
import { getNavigationInteractionId } from './navigation';
import { useUbiChatWindow } from './pages/useUbiChatWindow';

const getOrigin = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
};
const getOptions = (identifier: PageId, asPath: string) => ({ data: { identifier, uri: `${getOrigin()}${asPath}` } });

interface RootParameters {
  data: {
    identifier: string;
    uri: string;
  };
}

export const useUbiPage = <T>(
  pageId: PageId,
  entityUri: string,
  createPageFactory: (options: RootParameters) => T,
): { pageFactory: T } => {
  const ubiLogger = useUbiContext();
  const { asPath } = useRouter();

  useUbiChatWindow(getOptions(pageId, asPath));

  const pageFactory = useMemo(() => {
    const interactionId = getNavigationInteractionId(asPath);

    // TODO: Figure out how to properly emit navigation start events.
    const navigationStartInfo = interactionId
      ? {
          interactionId,
          navigationalRoot: pageId,
        }
      : {
          navigationReason: NavigationReason.CLIENT_STARTED,
          navigationalRoot: pageId,
        };
    ubiLogger.logNavigationStart(navigationStartInfo);
    ubiLogger.logNavigationEnd({ pageId, entityUri });
    return createPageFactory(getOptions(pageId, asPath));
  }, [asPath, createPageFactory, entityUri, pageId, ubiLogger]);
  return { pageFactory };
};
